// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConsentBanner
 * @package @geenius/analytics/react
 * @description Renders the React consent banner used to capture analytics
 * cookie preferences. The component updates provider-backed consent state and
 * emits the resulting settings through the provided callbacks.
 */

import type { ConsentSettings } from "@geenius/analytics";
import { Button, Card, CardContent } from "@geenius/ui/react";
import type React from "react";
import { useState } from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import { FadeIn, getFadeInTransition } from "../lib/motion";
import { useAnalyticsConsent } from "../provider";

/**
 * Props for rendering the React analytics consent banner.
 *
 * @property onAccept Called when the user grants analytics consent.
 * @property onReject Called when the user rejects analytics consent.
 * @property show Whether the banner should be visible on first render.
 * @property className Optional class name merged into the banner wrapper.
 */
export interface ConsentBannerProps {
  onAccept: (settings: ConsentSettings) => void;
  onReject: (settings: ConsentSettings) => void;
  show?: boolean;
  className?: string;
}

/**
 * Renders the React consent banner with accept and reject actions.
 *
 * @param props Banner callbacks and initial visibility.
 * @returns A consent banner element, or `null` after the user dismisses it.
 */
export function ConsentBanner(
  props: ConsentBannerProps,
): React.ReactElement | null {
  const [visible, setVisible] = useState(props.show !== false);
  const [preferences, setPreferences] = useState<
    Pick<ConsentSettings, "analytics" | "marketing" | "personalization">
  >({
    analytics: false,
    marketing: false,
    personalization: false,
  });
  const copy = useAnalyticsCopy();
  const consent = useAnalyticsConsent();

  const buildSettings = (
    settings: Pick<
      ConsentSettings,
      "analytics" | "marketing" | "personalization"
    >,
  ): ConsentSettings => ({
    essential: true,
    ...settings,
    timestamp: Date.now(),
  });

  const accept = (settings = preferences): void => {
    const consentSettings = buildSettings(settings);
    consent?.updateConsent(consentSettings);
    props.onAccept(consentSettings);
    setVisible(false);
  };

  const reject = (): void => {
    const consentSettings = buildSettings({
      analytics: false,
      marketing: false,
      personalization: false,
    });
    consent?.updateConsent(consentSettings);
    props.onReject(consentSettings);
    setVisible(false);
  };

  const updatePreference =
    (scope: keyof typeof preferences) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const checked = event.currentTarget.checked;
      setPreferences((current) => ({
        ...current,
        [scope]: checked,
      }));
    };

  if (!visible) return null;

  return (
    <FadeIn transition={getFadeInTransition()}>
      <section
        data-analytics="consent-banner"
        aria-label={copy.t("analytics.consent.regionLabel")}
      >
        <Card
          className={cn(
            "sticky bottom-4 border-muted/80 bg-muted/40",
            props.className,
          )}
        >
          <CardContent className="flex flex-col gap-4 pt-6">
            <p
              className="text-sm text-muted-foreground"
              data-analytics="consent-text"
            >
              {copy.t("analytics.consent.message")}
            </p>
            <fieldset
              className="grid gap-2 rounded-lg border border-border bg-background/70 p-3"
              data-analytics="consent-preferences"
            >
              <legend className="sr-only">
                {copy.t("analytics.consent.preferencesLegend")}
              </legend>
              {(
                [
                  ["personalization", "analytics.consent.personalization"],
                  ["analytics", "analytics.consent.analyticsScope"],
                  ["marketing", "analytics.consent.marketing"],
                ] as const
              ).map(([scope, labelKey]) => (
                <label
                  key={scope}
                  className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm text-foreground"
                >
                  <span>{copy.t(labelKey)}</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    checked={preferences[scope]}
                    onChange={updatePreference(scope)}
                  />
                </label>
              ))}
            </fieldset>
            <div
              className="flex flex-wrap gap-3"
              data-analytics="consent-actions"
            >
              <Button
                type="button"
                variant="primary"
                data-analytics="consent-accept"
                onClick={() =>
                  accept({
                    analytics: true,
                    marketing: true,
                    personalization: true,
                  })
                }
              >
                {copy.t("analytics.consent.acceptAll")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                data-analytics="consent-save"
                onClick={() => accept()}
              >
                {copy.t("analytics.consent.savePreferences")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                data-analytics="consent-reject"
                onClick={reject}
              >
                {copy.t("analytics.consent.reject")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </FadeIn>
  );
}
